import { useEffect, useRef } from 'react';

// Shortest the thumb gets, so it stays a grabbable target on a long list.
const MIN_THUMB_PX = 24;

/**
 * A drawn vertical scrollbar — track and thumb, both ordinary divs — beside a
 * vertically-scrolling element, driven by and driving that element.
 *
 * The vertical twin of useTopScrollbar, drawn for the same reason: iOS and
 * Android draw overlay scrollbars that fade out when idle and ignore
 * ::-webkit-scrollbar styling, so a styled native bar would be invisible on
 * the phones this is for. The scroller's own bar is hidden in CSS.
 *
 * While the content overflows, the wrapper gets `data-overflowing` (so the
 * CSS can leave room for the bar) and the track shows; otherwise both go.
 * During a drag the track gets `data-dragging`.
 *
 * @returns {{wrapRef: Object, scrollerRef: Object, contentRef: Object, trackRef: Object, thumbRef: Object}}
 *   refs for the wrapper, the scrolling element, the element inside it that
 *   holds the content, the track and the thumb
 */
export default function useSideScrollbar() {
  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!wrap || !scroller || !track || !thumb) return undefined;

    const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

    /**
     * Sizes read fresh each time: the list grows and shrinks as hours are
     * picked and court cards open.
     * @returns {{overflow: number, thumbHeight: number, travel: number}}
     */
    const metrics = () => {
      const overflow = scroller.scrollHeight - scroller.clientHeight;
      const trackHeight = track.clientHeight;
      const proportional = trackHeight * (scroller.clientHeight / scroller.scrollHeight || 1);
      const thumbHeight = clamp(Math.round(proportional), Math.min(MIN_THUMB_PX, trackHeight), trackHeight);
      return { overflow, thumbHeight, travel: trackHeight - thumbHeight };
    };

    let frame = 0;
    const render = () => {
      frame = 0;
      const { overflow, thumbHeight, travel } = metrics();
      // The 1px absorbs sub-pixel heights.
      if (overflow <= 1) {
        delete wrap.dataset.overflowing;
        return;
      }
      wrap.dataset.overflowing = 'true';
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${travel <= 0 ? 0 : (scroller.scrollTop / overflow) * travel}px)`;
    };
    // scroll fires faster than the screen refreshes; coalesce to a frame.
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(render); };

    // ---- drag / jump -----------------------------------------------------
    // null when idle, otherwise how far into the thumb the pointer landed.
    let grabOffset = null;
    const setDragging = (on) => {
      if (on) track.dataset.dragging = 'true';
      else delete track.dataset.dragging;
    };

    const scrollTo = (pointerY) => {
      const { overflow, travel } = metrics();
      if (travel <= 0) return;
      const y = pointerY - track.getBoundingClientRect().top - grabOffset;
      scroller.scrollTop = clamp(y / travel, 0, 1) * overflow;
    };

    const onPointerMove = (e) => { if (grabOffset !== null) scrollTo(e.clientY); };

    const onPointerUp = () => {
      grabOffset = null;
      setDragging(false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };

    const onPointerDown = (e) => {
      const { overflow, thumbHeight, travel } = metrics();
      if (overflow <= 0 || travel <= 0) return;
      const trackTop = track.getBoundingClientRect().top;
      const thumbTop = thumb.getBoundingClientRect().top - trackTop;
      const at = e.clientY - trackTop;
      // Grabbing the thumb keeps it under the same part of the finger;
      // pressing the bare track centres the thumb on that point.
      const onThumb = at >= thumbTop && at <= thumbTop + thumbHeight;
      grabOffset = onThumb ? at - thumbTop : thumbHeight / 2;
      if (!onThumb) scrollTo(e.clientY);
      setDragging(true);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
      // Stops the press turning into a page scroll or a text selection;
      // `touch-action: none` on the track is the other half.
      e.preventDefault();
    };

    const supportsPointer = typeof window.PointerEvent === 'function';
    if (supportsPointer) track.addEventListener('pointerdown', onPointerDown);

    render();
    scroller.addEventListener('scroll', schedule, { passive: true });

    // The scroller's box stops changing once it reaches its max height, so the
    // content is observed too: opening a court card grows scrollHeight only.
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(schedule);
      observer.observe(scroller);
      if (content) observer.observe(content);
    }
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (supportsPointer) track.removeEventListener('pointerdown', onPointerDown);
      onPointerUp();
      if (observer) observer.disconnect();
    };
  }, []);

  return { wrapRef, scrollerRef, contentRef, trackRef, thumbRef };
}
